@import 'tailwindcss';
@import 'uniwind';

/*
 * Remove Before Flight — "Field Manual" design system.
 * Static tokens (fonts) live in @theme; theme-adaptive colors live in
 * @layer theme with light/dark variants. Light is the canonical design.
 *
 * Font rule (Uniwind): one family name per token — no CSS fallback lists.
 * Family names come from @expo-google-fonts (loaded in app/_layout.tsx).
 */

@theme {
  /* Display & headings — Archivo */
  --font-display: 'Archivo_800ExtraBold';
  --font-heading: 'Archivo_700Bold';
  --font-heading-semibold: 'Archivo_600SemiBold';

  /* Body — Hanken Grotesk (one family per weight, RN has no synthetic weights) */
  --font-sans: 'HankenGrotesk_400Regular';
  --font-sans-medium: 'HankenGrotesk_500Medium';
  --font-sans-semibold: 'HankenGrotesk_600SemiBold';
  --font-sans-bold: 'HankenGrotesk_700Bold';

  /* Field-manual markers, timestamps, labels — Lexend SemiBold */
  --font-accent: 'Lexend_600SemiBold';
}

@layer theme {
  :root {
    @variant light {
      /* Surfaces — warm canvas */
      --color-background: #f3eee2;
      --color-card: #fbf8f0;
      --color-well: #ece3d0;

      /* Ink text */
      --color-foreground: #1b1916;
      --color-foreground-secondary: #6f6b63;
      --color-muted: #8a8275;
      --color-faint: #a39a8b;

      /* Borders */
      --color-border: #e2dac8;
      --color-border-strong: #d8cfb9;

      /* Signal accent */
      --color-primary: #d2402a;
      --color-signal: #d2402a;
      --color-on-signal: #ffffff;

      /* Ink surfaces (dark buttons / banners on light) */
      --color-ink: #1b1916;
      --color-on-ink: #f3eee2;

      /* Semantic + banner fills (from the StatusBanner spec) */
      --color-positive: #7d8a6e;
      --color-positive-bg: #e3e7d6;
      --color-positive-border: #c5d0ad;
      --color-caution: #c98a2e;
      --color-caution-bg: #f5e6c8;
      --color-caution-border: #e3cd9b;
      --color-alert: #d2402a;
      --color-alert-bg: #faece9;
      --color-alert-border: #e6c7bf;
      --color-info: #3f7d8a;

      /* Presence colors — collaborator focus claims (see CONTEXT.md). Hues
         steer clear of the signal red so a claim never reads as an error. */
      --color-presence-0: #3b6ea5;
      --color-presence-1: #7d5ba6;
      --color-presence-2: #4e8a4e;
      --color-presence-3: #a8781f;
      --color-presence-4: #b5527d;
      --color-presence-5: #3f8a7d;
      --color-presence-6: #78883a;
      --color-presence-7: #8a6a4f;
    }

    @variant dark {
      /* Deferred per design (light-first), but every var must exist. */
      --color-background: #14130f;
      --color-card: #1d1b16;
      --color-well: #24211a;

      --color-foreground: #f3eee2;
      --color-foreground-secondary: #b3a994;
      --color-muted: #9a9389;
      --color-faint: #6f6b63;

      --color-border: #2e2a22;
      --color-border-strong: #3c3932;

      --color-primary: #e1543d;
      --color-signal: #e1543d;
      --color-on-signal: #ffffff;

      --color-ink: #f3eee2;
      --color-on-ink: #1b1916;

      --color-positive: #9aa886;
      --color-positive-bg: #2a3122;
      --color-positive-border: #3d4631;
      --color-caution: #d9a24e;
      --color-caution-bg: #332813;
      --color-caution-border: #4a3a1d;
      --color-alert: #e1543d;
      --color-alert-bg: #331c17;
      --color-alert-border: #4a2820;
      --color-info: #5b9aa8;

      /* Presence colors — brighter than light mode so borders read on ink. */
      --color-presence-0: #6f9fd8;
      --color-presence-1: #a78bd0;
      --color-presence-2: #7dbd7d;
      --color-presence-3: #d9a84e;
      --color-presence-4: #d987ab;
      --color-presence-5: #6fbdae;
      --color-presence-6: #a8bd62;
      --color-presence-7: #bd9a78;
    }
  }
}
